import { useState } from "react";
import { MapPin, Factory } from "lucide-react";

const initialsOf = (name) => name.replace(/&/g, "").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("");

const Plate = ({ item, meta, testId }) => {
  const [failed, setFailed] = useState(!item.logo);
  const MetaIcon = meta === "sector" ? Factory : MapPin;
  return (
    <span
      data-testid={testId}
      className="group relative flex h-28 w-56 shrink-0 flex-col items-center justify-center gap-3 overflow-hidden border border-[#DDE4EA] bg-white px-5 shadow-[0_14px_35px_-28px_rgba(14,27,36,0.5)] transition-all duration-500 hover:-translate-y-1 hover:border-[#0F5C8C]/40 hover:shadow-[0_22px_45px_-28px_rgba(14,27,36,0.55)]"
    >
      <span className="flex h-12 w-full items-center justify-center">
        {failed ? (
          <span className="flex h-11 w-11 items-center justify-center border border-[#C8D3DB] font-display text-sm font-extrabold tracking-tight text-[#5E7280]">
            {initialsOf(item.name)}
          </span>
        ) : (
          <img
            src={item.logo}
            alt={`${item.name} logo`}
            loading="lazy"
            onError={() => setFailed(true)}
            data-testid={testId ? `${testId}-logo` : undefined}
            className={`${item.wide ? "max-h-9 max-w-[148px]" : "max-h-12 max-w-[64px]"} object-contain transition-transform duration-500 group-hover:scale-105`}
          />
        )}
      </span>
      <span className="text-center font-display text-[12px] font-bold uppercase leading-tight tracking-[0.12em] text-[#5E7280] transition-colors duration-500 group-hover:text-[#0E1B24]">
        {item.name}
      </span>
      <span
        data-testid={testId ? `${testId}-info` : undefined}
        className="pointer-events-none absolute inset-0 flex translate-y-full flex-col justify-center gap-2 bg-[#0E1B24] px-5 text-left transition-transform duration-500 ease-out group-hover:translate-y-0"
      >
        <span className="inline-flex items-center gap-1.5 font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#7FB2D4]">
          <MetaIcon size={11} /> {meta === "sector" ? item.sector : item.country}
        </span>
        <span className="text-[12px] font-medium leading-snug text-white">{item.scope}</span>
      </span>
    </span>
  );
};

// Shared logo wall: a paused-on-hover marquee of plates with an info panel.
export const LogoMarquee = ({ items, testId, plateId, index = "", label, title, lead, note, meta = "country" }) => (
  <section className="relative border-y border-[#DDE4EA] bg-[#EDF1F5] py-14 sm:py-20" data-testid={testId}>
    <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(#0E1B24 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
    <div className="relative mx-auto max-w-[1760px] px-4 sm:px-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="max-w-2xl">
          <p className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.28em] text-[#0F5C8C]">
            {index && <span className="font-mono2 text-[#5E7280]">{index}</span>} {label}
          </p>
          <h2 className="mt-3 font-display text-2xl font-extrabold leading-tight tracking-tight nv-head sm:text-3xl">{title}</h2>
        </div>
        <p className="max-w-sm text-sm leading-relaxed text-[#41535F]">{lead}</p>
      </div>
    </div>

    <div className="group/strip relative mt-10 overflow-hidden [mask-image:linear-gradient(90deg,transparent,#000_6%,#000_94%,transparent)]">
      <div className="nv-marquee-track flex w-max items-stretch gap-4 group-hover/strip:[animation-play-state:paused]">
        {items.map((item, i) => (
          <Plate key={item.name} item={item} meta={meta} testId={`${plateId}-${i}`} />
        ))}
        {items.map((item) => (
          <Plate key={`dup-${item.name}`} item={item} meta={meta} />
        ))}
      </div>
    </div>

    <div className="relative mx-auto mt-9 max-w-[1760px] px-4 sm:px-8">
      <p className="border-l-2 border-[#C8D3DB] pl-4 text-xs leading-relaxed text-[#6B7C88]" data-testid={`${testId}-note`}>{note}</p>
    </div>
  </section>
);

export default LogoMarquee;
